export type PurchaseWatchOptions = {
  findHash: () => Promise<`0x${string}` | null>;
  intervalMs?: number;
  timeoutMs?: number;
  sleep?: (ms: number) => Promise<void>;
};

export type PurchaseWatch = {
  /** Resolves with the tx hash once found. Never rejects; stays pending if stopped or timed out. */
  promise: Promise<`0x${string}`>;
  stop: () => void;
};

const defaultSleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Polls `findHash` until it returns a hash. Transient errors are swallowed.
 * Used as a fallback when a wallet mines a tx but never returns its hash.
 */
export function watchForPurchaseTx({
  findHash,
  intervalMs = 4000,
  timeoutMs,
  sleep = defaultSleep,
}: PurchaseWatchOptions): PurchaseWatch {
  let stopped = false;
  const stop = () => {
    stopped = true;
  };

  const promise = new Promise<`0x${string}`>((resolve) => {
    const startedAt = Date.now();
    const loop = async () => {
      while (!stopped) {
        await sleep(intervalMs);
        if (stopped) return;
        if (timeoutMs !== undefined && Date.now() - startedAt >= timeoutMs) {
          stopped = true;
          return;
        }
        try {
          const hash = await findHash();
          if (hash && !stopped) {
            stopped = true;
            resolve(hash);
            return;
          }
        } catch {
          // transient RPC error: retry on next tick
        }
      }
    };
    void loop();
  });

  return { promise, stop };
}
